/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { matchSelectableGuide } from "@packages/media";
import type { Track } from "@packages/sdk";
import { useRequestPrayer } from "@packages/sdk";
import type { HallowElement, HallowElementProps } from "@packages/ui";
import { numericPercentages, spacing } from "@packages/ui";
import { useEffect, useMemo, useState } from "react";

import { PrayerPlayButton } from "./PrayerPlayButton";
import PrayerSelectGuide from "./PrayerSelectGuide";
import PrayerSelectLength from "./PrayerSelectLength";

const hallowElement: HallowElement = "div";

const actionsContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: spacing.m,
  width: numericPercentages[100],
};

const selectionContainerStyles: Interpolation = {
  display: "flex",
  gap: spacing.s,
  width: numericPercentages[100],
};

type PrayerActionProps = HallowElementProps<typeof hallowElement> & {
  prayerId?: number;
};

const DEFAULT_PREFERRED_DURATION = 10 * 60 * 60;

const getDefaultLength = (
  lengthOptionsForGuide: Track[],
  previouslySelectedLength?: Track,
) => {
  const preferredDuration =
    previouslySelectedLength?.duration ?? DEFAULT_PREFERRED_DURATION;
  const preferredType = previouslySelectedLength?.media_type ?? "audio";
  const preferredDurationLengthOption = lengthOptionsForGuide.find(
    (lengthOption) =>
      lengthOption.media_type === preferredType &&
      lengthOption.duration >= preferredDuration - 50 &&
      lengthOption.duration <= preferredDuration + 50,
  );

  return preferredDurationLengthOption ?? lengthOptionsForGuide[0];
};

export const PrayerControls = ({ prayerId }: PrayerActionProps) => {
  const { query: queryPrayer } = useRequestPrayer({
    id: prayerId ? parseInt(prayerId.toString()) : null,
    retryOnMount: false,
    retry: 0,
  });

  const [selectedGuides, setSelectedGuides] = useState<number[] | null>(null);

  const lengthOptions = useMemo(
    () =>
      queryPrayer.data?.selectable_guides?.find((g) =>
        matchSelectableGuide(g.guide_ids, selectedGuides),
      )?.tracks ?? [],
    [queryPrayer.data, selectedGuides],
  );

  const [selectedLength, setSelectedLength] = useState<Track | null>(null);

  const selectedTrack =
    lengthOptions?.find((track) => track.id === selectedLength?.id) ?? null;

  useEffect(() => {
    if (queryPrayer.data && !selectedGuides) {
      setSelectedGuides(
        queryPrayer.data.selectable_guides?.[0]?.guide_ids ?? null,
      );
    }
  }, [queryPrayer.data]);

  useEffect(() => {
    if (lengthOptions.length) {
      setSelectedLength(
        (previouslySelectedLength) =>
          lengthOptions.find(
            (trk) => trk.id === queryPrayer.data?.selected_track_id,
          ) ?? getDefaultLength(lengthOptions, previouslySelectedLength),
      );
    }
  }, [lengthOptions, selectedGuides]);

  return (
    <div css={actionsContainerStyles}>
      <div css={selectionContainerStyles}>
        <PrayerSelectGuide
          selectedGuides={selectedGuides}
          setSelectedGuides={setSelectedGuides}
          options={queryPrayer?.data?.selectable_guides}
        />
        <PrayerSelectLength
          selectedLength={selectedLength}
          setSelectedLength={setSelectedLength}
          options={lengthOptions}
        />
      </div>
      <PrayerPlayButton
        prayer={queryPrayer.data}
        track={selectedTrack}
        isSong={queryPrayer.data?.is_song}
      />
    </div>
  );
};
